import React, { useEffect, useState } from "react";
import { getAnonUserAnalysis, getLeagueCategories } from "../../../api";
import TableLoader from "./TableLoader";
import Select from "react-select";

export const AnonUserAnalysis = () => {
  const [categoryOptions, setCategoryOptions] = useState([
    {
      value: 0,
      label: "All Regions",
    },
  ]);
  const [errors, setErrors] = useState([]);
  const [leagueCategory, setLeagueCategory] = useState(0);
  const [loading, setLoading] = useState(false);
  const [anonAnalysis, setAnonAnalysis] = useState([]);

  useEffect(() => {
    async function getLeagueCats() {
      const response = await getLeagueCategories();
      if (response.status === 200) {
        setCategoryOptions((prev) => [...prev, ...response.data.data]);
      }

      if (response.status === 400) {
        setErrors((prev) => ({
          ...prev,
          region: "No regions found",
        }));
      }
    }
    getLeagueCats();
  }, []);

  useEffect(() => {
    async function fetchAnonAnalysis() {
      try {
        setLoading(true);
        const response = await getAnonUserAnalysis({
          region: leagueCategory,
        });

        if (response.status === 200) {
          setAnonAnalysis(response?.data?.data?.[0] || []);
        }
      } catch (error) {
        // Optionally, you can show an error message to the user
      } finally {
        setLoading(false);
      }
    }
    fetchAnonAnalysis();
  }, [leagueCategory]);

  const ProgressBar = ({ percentage, color = "primary" }) => {
    // Ensure percentage is a number and clamp it between 0 and 100
    const validPercentage = Math.min(Math.max(Number(percentage) || 0, 0), 100);

    return (
      <div className="progress" style={{ height: "1rem" }}>
        <div
          className={`progress-bar bg-${color}`}
          role="progressbar"
          style={{ width: `${validPercentage}%` }}
          aria-valuenow={validPercentage}
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>
    );
  };

  const analyzeData = () => {
    if (!anonAnalysis || Object.keys(anonAnalysis).length === 0) {
      return {
        totalUsers: 0,
        convertedUsers: 0,
        conversionRate: 0,
        stepCompletion: {
          "Select Tournament": { completed: 0, started: 0 },
          "Provide Team Name": { completed: 0, started: 0 },
          "Select Players": { completed: 0, started: 0 },
          Register: { completed: 0, started: 0 },
        },
      };
    }

    const stepCompletion = {};
    anonAnalysis.step_analysis.forEach((stepData) => {
      let stepName;
      switch (stepData.step) {
        case "select_tournament":
          stepName = "Select Tournament";
          break;
        case "provide_team_name":
          stepName = "Provide Team Name";
          break;
        case "select_players":
          stepName = "Select Players";
          break;
        case "register":
          stepName = "Register";
          break;
        default:
          stepName = stepData.step;
      }
      stepCompletion[stepName] = {
        started: stepData.started || 0,
        completed: stepData.completed || 0,
        completionRate: stepData.completion_rate || 0,
        dropoffRate: stepData.dropoff_rate || 0,
      };
    });

    return {
      totalUsers: parseInt(anonAnalysis.total_anonymous_visitors) || 0,
      convertedUsers: parseInt(anonAnalysis.converted_users) || 0,
      conversionRate: parseFloat(anonAnalysis.overall_conversion_rate) || 0,
      stepCompletion,
    };
  };

  if (loading) {
    return <TableLoader />;
  }

  const analysis = analyzeData();

  return (
    <div className="p-4 bg-white rounded shadow">
      <div className="d-flex flex-column w-100 gap-2 card-body">
        {/* Region Selection */}
        <div className="text-center mb-4">
          <Select
            value={categoryOptions.find(
              (option) => option.value === leagueCategory
            )}
            onChange={(e) => setLeagueCategory(e.value)}
            options={categoryOptions}
            placeholder="Select Region"
          />
        </div>

        {/* Overall Conversion */}
        <div className="mb-4 p-3 border rounded">
          <h2 className="h5 font-weight-bold">Overall Conversion Rate</h2>
          <div className="d-flex align-items-center mt-2">
            <div className="w-25 font-weight-bold h5">
              {analysis.conversionRate.toFixed(1)}%
            </div>
            <div className="flex-grow-1 ml-2">
              <ProgressBar percentage={analysis.conversionRate} />
            </div>
          </div>
          <div className="text-muted small mt-1">
            {analysis.convertedUsers} of {analysis.totalUsers} anonymous users
            converted to registered users
          </div>
        </div>

        {/* Step-by-step Completion Analysis */}
        <div className="mb-4 table-responsive">
          <h2 className="h5 font-weight-bold mb-2">Anonymous User Analysis</h2>
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th scope="col" className="text-left">
                  Step
                </th>
                <th scope="col" className="text-left">
                  Started
                </th>
                <th scope="col" className="text-left">
                  Completed
                </th>
                <th scope="col" className="text-left">
                  Completion Rate
                </th>
                <th scope="col" className="text-left">
                  Dropoff Rate
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(analysis.stepCompletion).map(
                ([step, data], index) => {
                  const started = data.started || 0;
                  const completed = data.completed || 0;
                  const completionRate = data.completionRate || 0;
                  const dropoffRate = data.dropoffRate || 0;

                  return (
                    <tr
                      key={step}
                      className={index % 2 === 0 ? "table-active" : ""}
                    >
                      <td>{step}</td>
                      <td>{started}</td>
                      <td>{completed}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className="me-2" style={{ minWidth: "60px" }}>
                            {completionRate.toFixed(1)}%
                          </span>
                          <div className="flex-grow-1">
                            <ProgressBar percentage={completionRate} />
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <span
                            className={`me-2 ${
                              dropoffRate > 20 ? "fw-bold text-danger" : ""
                            }`}
                            style={{ minWidth: "60px" }}
                          >
                            {dropoffRate.toFixed(1)}%
                          </span>
                          <div className="flex-grow-1">
                            <ProgressBar
                              percentage={dropoffRate}
                              color="danger"
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                }
              )}
            </tbody>
          </table>
        </div>

        <div className="text-muted small mt-4">
          * Data shown is based on {analysis.totalUsers} unique anonymous
          visitors
        </div>
      </div>
    </div>
  );
};
